import { defineStore } from 'pinia';
import { ref, computed } from 'vue';
import { api, type Item, type CreateItemInput, type UpdateItemInput } from '../api';
import { useToastStore } from './toast';
import { useHistoryStore } from './history';
import { useUndoStore } from './undo';
import { useTagsStore } from './tags';

export const useItemsStore = defineStore('items', () => {
  const items = ref<Item[]>([]);
  const loading = ref(false);
  const error = ref<string | null>(null);
  const history = useHistoryStore();

  // 批量选择
  const selectedIds = ref<Set<number>>(new Set());

  // 批量操作状态
  const batchOperationProgress = ref<{ current: number; total: number } | null>(null);
  const isBatchOperationRunning = ref(false);

  // 请求去重：存储正在进行的请求
  const pendingRequests = new Map<string, Promise<any>>();

  const pendingItems = computed(() =>
    items.value.filter(item => !item.is_completed && !item.is_archived)
  );

  const completedItems = computed(() =>
    items.value.filter(item => item.is_completed && !item.is_archived)
  );

  const archivedItems = computed(() =>
    items.value.filter(item => item.is_archived)
  );

  const focusItems = computed(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    return items.value.filter(item => {
      // 排除已完成和归档的项目
      if (item.is_completed || item.is_archived) return false;

      // 包含标记为今日专注的项目
      if (item.is_focus) return true;

      // 包含今天到期的项目
      if (item.due_date) {
        const dueDate = new Date(item.due_date);
        dueDate.setHours(0, 0, 0, 0);
        const diffTime = dueDate.getTime() - today.getTime();
        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
        if (diffDays <= 1) return true; // 今天或明天到期
      }

      // 包含高优先级的项目
      if (item.priority === 'high') return true;

      return false;
    });
  });

  const pendingCount = computed(() => pendingItems.value.length);

  // 刷新标签统计
  function refreshTags() {
    try {
      const tagsStore = useTagsStore();
      tagsStore.extractTagsFromItems(items.value);
    } catch (e) {
      // 忽略错误
      console.warn('Failed to refresh tags:', e);
    }
  }

  // 请求去重辅助函数
  async function dedupeRequest<T>(key: string, requestFn: () => Promise<T>): Promise<T> {
    // 如果请求正在进行，返回现有的 promise
    if (pendingRequests.has(key)) {
      return pendingRequests.get(key) as Promise<T>;
    }

    // 创建新的请求 promise
    const promise = requestFn().finally(() => {
      // 请求完成后从 map 中移除
      pendingRequests.delete(key);
    });

    // 存储请求
    pendingRequests.set(key, promise);
    return promise;
  }

  async function fetchItems() {
    loading.value = true;
    error.value = null;
    try {
      const response = await api.getAllItems();
      if (response.success) {
        items.value = response.data;
        // 刷新标签统计
        refreshTags();

        // 自动归档 7 天前已完成的任务（静默执行，不显示确认对话框）
        autoArchiveCompleted(7).catch(() => {
          // 静默失败，不影响用户体验
        });
      }
    } catch (e: any) {
      error.value = e instanceof Error ? e.message : 'Failed to fetch items';
      const toast = useToastStore();

      // 处理 401 未认证错误
      if (e.code === 'UNAUTHORIZED' || e.status === 401) {
        if (import.meta.env.PROD) {
          toast.error('请先登录以使用 SnapClip');
        } else {
          console.warn('Authentication required - this is expected in local development without Cloudflare Access');
        }
      } else {
        toast.error('加载数据失败，请刷新重试');
      }
    } finally {
      loading.value = false;
    }
  }

  // 乐观更新：创建项目
  async function createItem(input: CreateItemInput) {
    // 保存当前状态到历史
    history.pushState(items.value);

    // 生成请求 key（基于内容）
    const requestKey = `create-${JSON.stringify(input)}`;

    return dedupeRequest(requestKey, async () => {
      // 创建临时项目（乐观更新）
      const tempId = Date.now();
      const tempItem: Item = {
        id: tempId as unknown as number, // 临时 ID
        content: input.content,
        url: input.url,
        source: input.source,
        notes: input.notes,
        is_completed: false,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
      };

      // 立即添加到列表
      items.value.unshift(tempItem);

      try {
        const response = await api.createItem(input);
        if (response.success) {
          // 用真实数据替换临时数据
          const index = items.value.findIndex(item => item.id === tempId);
          if (index !== -1) {
            items.value[index] = response.data;
          }
          // 刷新标签统计
          refreshTags();
          return true;
        } else {
          // API 返回失败，回滚
          items.value = items.value.filter(item => item.id !== tempId);
          return false;
        }
      } catch (e) {
        // 请求失败，回滚
        items.value = items.value.filter(item => item.id !== tempId);
        error.value = e instanceof Error ? e.message : 'Failed to create item';
        return false;
      }
    });
  }

  async function updateItem(id: number, input: UpdateItemInput) {
    // 保存当前状态到历史
    history.pushState(items.value);

    loading.value = true;
    error.value = null;
    try {
      const response = await api.updateItem(id, input);
      if (response.success) {
        const index = items.value.findIndex(item => item.id === id);
        if (index !== -1) {
          items.value[index] = response.data;
        }
        // 刷新标签统计
        refreshTags();
      }
      return response.success;
    } catch (e) {
      error.value = e instanceof Error ? e.message : 'Failed to update item';
      return false;
    } finally {
      loading.value = false;
    }
  }

  // 乐观更新：切换完成状态
  async function toggleItem(id: number) {
    const toast = useToastStore();

    // 保存当前状态到历史
    history.pushState(items.value);

    // 生成请求 key（基于操作类型和 ID）
    const requestKey = `toggle-${id}`;

    return dedupeRequest(requestKey, async () => {
      // 找到当前项目
      const index = items.value.findIndex(item => item.id === id);
      if (index === -1) return false;

      const item = items.value[index];
      const originalStatus = item.is_completed;

      // 立即更新 UI
      item.is_completed = !originalStatus;

      try {
        const response = await api.toggleItem(id);
        if (response.success) {
          // 同步服务器返回的数据
          items.value[index] = response.data;
          return true;
        } else {
          // API 返回失败，回滚
          item.is_completed = originalStatus;
          return false;
        }
      } catch (e) {
        // 请求失败，回滚
        item.is_completed = originalStatus;
        error.value = e instanceof Error ? e.message : 'Failed to toggle item';
        toast.error('操作失败，请重试');
        return false;
      }
    });
  }

  // 乐观更新：删除项目（支持撤销）
  async function deleteItem(id: number) {
    const toast = useToastStore();
    const undoStore = useUndoStore();

    // 保存原始数据
    const originalItem = items.value.find(item => item.id === id);
    if (!originalItem) return false;

    const originalIndex = items.value.findIndex(item => item.id === id);

    // 立即从列表移除
    items.value = items.value.filter(item => item.id !== id);

    // 保存到 undo store
    const undoId = undoStore.addAction({
      type: 'delete',
      items: [originalItem],
    });

    // 显示带撤销按钮的 toast
    toast.successWithUndo(
      `已删除：${originalItem.content.slice(0, 30)}${originalItem.content.length > 30 ? '...' : ''}`,
      async () => {
        // 撤销操作
        const restoredItems = undoStore.executeUndo(undoId);
        if (restoredItems) {
          items.value = [...items.value, ...restoredItems];
          // 刷新标签统计
          refreshTags();
          toast.info('已撤销删除');
        }
      },
      '已删除'
    );

    // 执行实际的删除请求（后台进行）
    try {
      const response = await api.deleteItem(id);
      if (!response.success) {
        // API 返回失败，自动撤销
        const restoredItems = undoStore.executeUndo(undoId);
        if (restoredItems) {
          items.value = [...items.value, ...restoredItems];
        }
        toast.error('删除失败');
        return false;
      }
      // 刷新标签统计
      refreshTags();
      return true;
    } catch (e) {
      // 请求失败，自动撤销
      const restoredItems = undoStore.executeUndo(undoId);
      if (restoredItems) {
        items.value = [...items.value, ...restoredItems];
      }
      error.value = e instanceof Error ? e.message : 'Failed to delete item';
      toast.error('删除失败');
      return false;
    }
  }

  // 乐观更新：清除已完成
  async function clearCompleted() {
    const toast = useToastStore();

    // 保存当前状态到历史
    history.pushState(items.value);

    // 生成请求 key
    const requestKey = 'clear-completed';

    return dedupeRequest(requestKey, async () => {
      // 保存已完成的项目用于回滚
      const completedItemsList = items.value.filter(item => item.is_completed);

      // 立即从列表移除
      items.value = items.value.filter(item => !item.is_completed);

      try {
        const response = await api.clearCompleted();
        if (response.success) {
          return true;
        } else {
          // API 返回失败，回滚
          items.value = [...items.value, ...completedItemsList];
          return false;
        }
      } catch (e) {
        // 请求失败，回滚
        items.value = [...items.value, ...completedItemsList];
        error.value = e instanceof Error ? e.message : 'Failed to clear completed';
        toast.error('清除失败，请重试');
        return false;
      }
    });
  }

  // 撤销
  function undo() {
    const previousItems = history.undo(items.value);
    if (previousItems) {
      items.value = previousItems;
      return true;
    }
    return false;
  }

  // 重做
  function redo() {
    const nextItems = history.redo(items.value);
    if (nextItems) {
      items.value = nextItems;
      return true;
    }
    return false;
  }

  // 批量选择：切换选择状态
  function toggleSelection(id: number) {
    if (selectedIds.value.has(id)) {
      selectedIds.value.delete(id);
    } else {
      selectedIds.value.add(id);
    }
    // 触发响应式更新
    selectedIds.value = new Set(selectedIds.value);
  }

  // 批量选择：清除所有选择
  function clearSelection() {
    selectedIds.value.clear();
    selectedIds.value = new Set();
  }

  // 批量选择：全选
  function selectAll() {
    items.value.forEach(item => selectedIds.value.add(item.id));
    selectedIds.value = new Set(selectedIds.value);
  }

  // 批量操作：标记完成
  async function batchComplete() {
    const toast = useToastStore();
    const ids = Array.from(selectedIds.value);
    if (ids.length === 0) return false;

    // 确认对话框
    if (!confirm(`确定要标记 ${ids.length} 项为完成吗？`)) {
      return false;
    }

    // 保存当前状态到历史
    history.pushState(items.value);

    isBatchOperationRunning.value = true;
    let successCount = 0;

    // 分批处理，避免一次性发送太多请求
    const batchSize = 10;
    for (let i = 0; i < ids.length; i += batchSize) {
      const batch = ids.slice(i, i + batchSize);
      batchOperationProgress.value = { current: i, total: ids.length };

      const results = await Promise.all(
        batch.map(id => toggleItem(id))
      );
      successCount += results.filter(r => r).length;
    }

    batchOperationProgress.value = { current: ids.length, total: ids.length };
    isBatchOperationRunning.value = false;

    if (successCount === ids.length) {
      toast.success(`已标记 ${successCount} 项为完成`);
      clearSelection();
      return true;
    } else {
      toast.error(`部分操作失败：${successCount}/${ids.length} 成功`);
      return false;
    }
  }

  // 批量操作：标记未完成
  async function batchUncomplete() {
    const toast = useToastStore();
    const ids = Array.from(selectedIds.value);
    if (ids.length === 0) return false;

    // 确认对话框
    if (!confirm(`确定要标记 ${ids.length} 项为未完成吗？`)) {
      return false;
    }

    // 保存当前状态到历史
    history.pushState(items.value);

    isBatchOperationRunning.value = true;
    let successCount = 0;

    // 分批处理
    const batchSize = 10;
    for (let i = 0; i < ids.length; i += batchSize) {
      const batch = ids.slice(i, i + batchSize);
      batchOperationProgress.value = { current: i, total: ids.length };

      const results = await Promise.all(
        batch.map(id => toggleItem(id))
      );
      successCount += results.filter(r => r).length;
    }

    batchOperationProgress.value = { current: ids.length, total: ids.length };
    isBatchOperationRunning.value = false;

    if (successCount === ids.length) {
      toast.success(`已标记 ${successCount} 项为未完成`);
      clearSelection();
      return true;
    } else {
      toast.error(`部分操作失败：${successCount}/${ids.length} 成功`);
      return false;
    }
  }

  // 批量操作：删除（支持撤销）
  async function batchDelete() {
    const toast = useToastStore();
    const undoStore = useUndoStore();
    const ids = Array.from(selectedIds.value);
    if (ids.length === 0) return false;

    // 查找要删除的项目
    const itemsToDelete = items.value.filter(item => ids.includes(item.id));

    // 保存到 undo store
    const undoId = undoStore.addAction({
      type: 'batch-delete',
      items: itemsToDelete,
    });

    // 立即从列表移除
    items.value = items.value.filter(item => !ids.includes(item.id));

    // 显示带撤销按钮的 toast
    toast.successWithUndo(
      `已删除 ${ids.length} 项`,
      async () => {
        // 撤销操作
        const restoredItems = undoStore.executeUndo(undoId);
        if (restoredItems) {
          items.value = [...items.value, ...restoredItems];
          // 刷新标签统计
          refreshTags();
          toast.info(`已撤销删除 ${restoredItems.length} 项`);
        }
      },
      `已删除 ${ids.length} 项`
    );

    // 执行实际的删除请求（后台进行）
    isBatchOperationRunning.value = true;
    let successCount = 0;

    // 分批处理
    const batchSize = 10;
    for (let i = 0; i < ids.length; i += batchSize) {
      const batch = ids.slice(i, i + batchSize);
      batchOperationProgress.value = { current: i, total: ids.length };

      const results = await Promise.all(
        batch.map(async (id) => {
          try {
            const response = await api.deleteItem(id);
            return response.success;
          } catch {
            return false;
          }
        })
      );
      successCount += results.filter(r => r).length;
    }

    batchOperationProgress.value = { current: ids.length, total: ids.length };
    isBatchOperationRunning.value = false;

    if (successCount === ids.length) {
      // 全部成功，清除选择
      clearSelection();
      // 刷新标签统计
      refreshTags();
      return true;
    } else {
      // 部分失败，显示错误
      const restoredItems = undoStore.executeUndo(undoId);
      if (restoredItems) {
        // 自动撤销，恢复未成功删除的项目
        items.value = [...items.value, ...restoredItems];
        // 刷新标签统计
        refreshTags();
      }
      toast.error(`部分操作失败：${successCount}/${ids.length} 成功，已自动撤销`);
      return false;
    }
  }

  // 批量操作：设置优先级
  async function batchSetPriority(priority: 'low' | 'medium' | 'high') {
    const toast = useToastStore();
    const ids = Array.from(selectedIds.value);
    if (ids.length === 0) return false;

    // 保存当前状态到历史
    history.pushState(items.value);

    const results = await Promise.all(
      ids.map(id => updateItem(id, { priority }))
    );

    const successCount = results.filter(r => r).length;
    if (successCount === ids.length) {
      toast.success(`已设置 ${successCount} 项的优先级`);
      clearSelection();
      return true;
    } else {
      toast.error(`部分操作失败：${successCount}/${ids.length} 成功`);
      return false;
    }
  }

  // 批量操作：设置标签
  async function batchSetTags(tags: string[]) {
    const toast = useToastStore();
    const ids = Array.from(selectedIds.value);
    if (ids.length === 0) return false;

    // 保存当前状态到历史
    history.pushState(items.value);

    const results = await Promise.all(
      ids.map(id => updateItem(id, { tags }))
    );

    const successCount = results.filter(r => r).length;
    if (successCount === ids.length) {
      toast.success(`已设置 ${successCount} 项的标签`);
      clearSelection();
      return true;
    } else {
      toast.error(`部分操作失败：${successCount}/${ids.length} 成功`);
      return false;
    }
  }

  // 批量操作：设置截止日期
  async function batchSetDueDate(dueDate: string) {
    const toast = useToastStore();
    const ids = Array.from(selectedIds.value);
    if (ids.length === 0) return false;

    // 保存当前状态到历史
    history.pushState(items.value);

    const results = await Promise.all(
      ids.map(id => updateItem(id, { due_date: dueDate }))
    );

    const successCount = results.filter(r => r).length;
    if (successCount === ids.length) {
      toast.success(`已设置 ${successCount} 项的截止日期`);
      clearSelection();
      return true;
    } else {
      toast.error(`部分操作失败：${successCount}/${ids.length} 成功`);
      return false;
    }
  }

  // 切换今日专注状态
  async function toggleFocus(id: number) {
    const toast = useToastStore();
    const item = items.value.find(i => i.id === id);
    if (!item) return false;

    const newFocusStatus = !item.is_focus;
    const success = await updateItem(id, { is_focus: newFocusStatus });

    if (success) {
      toast.success(newFocusStatus ? '已添加到今日专注' : '已从今日专注移除');
    }

    return success;
  }

  // 归档项目
  async function archiveItem(id: number) {
    const toast = useToastStore();
    const success = await updateItem(id, { is_archived: true });

    if (success) {
      toast.success('已归档');
      // 刷新标签统计
      refreshTags();
    }

    return success;
  }

  // 批量归档
  async function batchArchive() {
    const toast = useToastStore();
    const ids = Array.from(selectedIds.value);
    if (ids.length === 0) return false;

    // 保存当前状态到历史
    history.pushState(items.value);

    const results = await Promise.all(
      ids.map(id => updateItem(id, { is_archived: true }))
    );

    const successCount = results.filter(r => r).length;
    if (successCount === ids.length) {
      toast.success(`已归档 ${successCount} 项`);
      clearSelection();
      // 刷新标签统计
      refreshTags();
      return true;
    } else {
      toast.error(`部分操作失败：${successCount}/${ids.length} 成功`);
      return false;
    }
  }

  // 恢复归档项目
  async function unarchiveItem(id: number) {
    const toast = useToastStore();
    const success = await updateItem(id, { is_archived: false });

    if (success) {
      toast.success('已恢复');
    }

    return success;
  }

  // 自动归档已完成的任务（N天前）
  async function autoArchiveCompleted(days: number = 7) {
    const toast = useToastStore();
    const cutoffDate = new Date();
    cutoffDate.setDate(cutoffDate.getDate() - days);
    const cutoffDateIso = cutoffDate.toISOString();

    // 找到需要归档的项目
    const itemsToArchive = items.value.filter(item => {
      if (!item.is_completed || item.is_archived) return false;
      const updatedDate = new Date(item.updated_at);
      return updatedDate < new Date(cutoffDateIso);
    });

    // 没有需要归档的项目，静默返回
    if (itemsToArchive.length === 0) {
      return 0;
    }

    // 批量归档（静默执行）
    const results = await Promise.all(
      itemsToArchive.map(item => updateItem(item.id, { is_archived: true }))
    );

    const successCount = results.filter(r => r).length;
    if (successCount > 0) {
      // 只有在有归档项时才显示提示
      toast.success(`已自动归档 ${successCount} 项已完成任务（${days}天前）`);
      // 刷新标签统计
      refreshTags();
    }

    return successCount;
  }

  return {
    items,
    loading,
    error,
    pendingItems,
    completedItems,
    archivedItems,
    focusItems,
    pendingCount,
    selectedIds,
    batchOperationProgress,
    isBatchOperationRunning,
    fetchItems,
    createItem,
    updateItem,
    toggleItem,
    deleteItem,
    clearCompleted,
    undo,
    redo,
    toggleSelection,
    clearSelection,
    selectAll,
    batchComplete,
    batchUncomplete,
    batchDelete,
    batchSetPriority,
    batchSetTags,
    batchSetDueDate,
    toggleFocus,
    archiveItem,
    batchArchive,
    unarchiveItem,
    autoArchiveCompleted,
  };
});
